import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useTicketMarkReadStatus, useTicketMarkUnReadStatus } from 'hooks/api/useTickets';

interface TicketsReadStatusHookProps {
  selectedTicketsIds: string[];
}

interface TicketsReadStatusHookResult {
  errorIsVisible: boolean;
  selectedOption: SelectOptionT;
  readStatusTicketErrorMessage: string | undefined;
  unreadStatusTicketErrorMessage: string | undefined;
  handlePopupOpen: () => void;
  handlePopupClose: () => void;
  handleOptionChange: (newValue: SelectOptionT | null) => SelectOptionT | null;
  isPopupOpen: boolean;
}

function useTicketsReadStatus({
  selectedTicketsIds,
}: TicketsReadStatusHookProps): TicketsReadStatusHookResult {
  const queryClient = useQueryClient();
  const [IsPopupOpen, setPopupVisibility] = useState<boolean>(false);
  const customDefaultValue: SelectOptionT = { value: '', label: '' };
  const [selectedOption, setSelectedOption] = useState<SelectOptionT>(customDefaultValue);

  const markStatusAsReadMutation = useTicketMarkReadStatus({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
    },
  });

  const markStatusAsUnReadMutation = useTicketMarkUnReadStatus({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
    },
  });

  const readStatusTicketErrorMessage = useMemo(() => {
    setPopupVisibility(true);
    return getValidationErrorMessage(markStatusAsReadMutation.error);
  }, [markStatusAsReadMutation.error]);

  const unreadStatusTicketErrorMessage = useMemo(() => {
    setPopupVisibility(true);

    return getErrorMessage(markStatusAsUnReadMutation.error);
  }, [markStatusAsUnReadMutation.error]);

  const handleOptionSelection = useCallback(
    (selectedOption: SelectOptionT) => {
      if (selectedOption.toString() === 'read') {
        markStatusAsReadMutation.mutate({
          ticketIds: selectedTicketsIds,
        });
      } else if (selectedOption.toString() === 'unread') {
        markStatusAsUnReadMutation.mutate({ ticketIds: selectedTicketsIds });
      }
    },
    [markStatusAsReadMutation, markStatusAsUnReadMutation, selectedTicketsIds]
  );

  const handlePopupOpen = (): void => {
    setPopupVisibility(true);
  };

  const handlePopupClose = (): void => {
    setPopupVisibility(false);
  };

  const handleOptionChange = (newValue: SelectOptionT | null): SelectOptionT | null => {
    setSelectedOption(newValue || customDefaultValue);
    if (newValue !== null) {
      handleOptionSelection(newValue);
    }
    return newValue;
  };

  return {
    errorIsVisible: markStatusAsReadMutation.isError || markStatusAsUnReadMutation.isError,
    selectedOption,
    isPopupOpen: IsPopupOpen,
    readStatusTicketErrorMessage,
    unreadStatusTicketErrorMessage,
    handlePopupOpen,
    handlePopupClose,
    handleOptionChange,
  };
}

export default useTicketsReadStatus;
